import { useRef } from "react";
import { Dimensions, Modal, Pressable, StyleSheet, View } from "react-native";
import { Button, IconButton, Text } from "react-native-paper";
import SignatureCanvas from "react-signature-canvas";

import { colors } from "@/lib/theme";

interface Props {
    visible: boolean;
    onDone: (base64: string) => void;
    onClose: () => void;
    title?: string;
    /** Label of the confirm button; "Submit" where confirming also saves. */
    confirmLabel?: string;
}

// Canvas attribute dimensions must be explicit numbers so the drawing
// coordinate space matches the rendered size (otherwise strokes drift).
const CANVAS_WIDTH = Math.min(Dimensions.get("window").width - 64, 460);
const CANVAS_HEIGHT = 220;

/**
 * Real web signature capture using react-signature-canvas (HTML canvas).
 * Replaces the old 1px transparent-PNG fallback so the signature actually gets
 * stored. Native uses SignatureModal (WebView canvas) instead — Metro resolves
 * the non-`.web` stub there.
 */
export function WebSignaturePad({ visible, onDone, onClose, title, confirmLabel }: Props) {
    const ref = useRef<SignatureCanvas>(null);

    const handleClear = () => {
        ref.current?.clear();
    };

    const handleDone = () => {
        const pad = ref.current;
        if (!pad || pad.isEmpty()) {
            onClose();
            return;
        }
        const dataUrl = pad.getTrimmedCanvas().toDataURL("image/png");
        onDone(dataUrl);
    };

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={styles.overlay}>
                <Pressable style={styles.backdrop} onPress={onClose} />
                <View style={styles.card}>
                    <View style={styles.header}>
                        <Text style={styles.title}>{title ?? "Sign here"}</Text>
                        <IconButton icon="close" size={20} onPress={onClose} />
                    </View>
                    <View style={styles.canvasWrapper}>
                        <SignatureCanvas
                            ref={ref}
                            penColor={colors.textPrimary}
                            backgroundColor="#FFFFFF"
                            canvasProps={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
                        />
                    </View>
                    <View style={styles.footer}>
                        <Button mode="outlined" onPress={handleClear}>
                            Clear
                        </Button>
                        <Button mode="contained" onPress={handleDone}>
                            {confirmLabel ?? "Done"}
                        </Button>
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        padding: 24
    },
    backdrop: {
        ...StyleSheet.absoluteFill,
        backgroundColor: "rgba(0,0,0,0.4)"
    },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 16,
        overflow: "hidden",
        width: CANVAS_WIDTH + 24
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.surfaceBorder
    },
    title: {
        fontSize: 16,
        fontWeight: "600",
        color: colors.textPrimary
    },
    canvasWrapper: {
        borderWidth: 1,
        borderColor: "#DDD",
        margin: 12,
        borderRadius: 8,
        overflow: "hidden",
        alignSelf: "center"
    },
    footer: {
        flexDirection: "row",
        justifyContent: "space-between",
        paddingHorizontal: 16,
        paddingBottom: 20,
        paddingTop: 4
    }
});
